import { DateFormat, now } from "common";
import dayjs from "dayjs";
import { useMemo } from "react";
import { BaseScheduleRepeat, Schedule } from "types";
import { includesDate } from "./BaseSchedule";
import { getActiveBlackoutSchedules, isActive } from "./Schedule";
import { getAvailableSchedules } from "@/models/Experience";
import { getSeller } from "@/utils/getSeller";

export enum BookabilityStatus {
    BOOKABLE = "bookable",
    EXPIRED = "expired",
    FULL_BLACKOUT = "full-blackout",
}

/**
 * Check if a blackout completely covers a schedule's active period.
 * For WEEKLY schedules: blackout must encompass the schedule's future date range
 *   (from max(now, schedule.start) onwards)
 * For CUSTOM schedules: blackout must cover all specific dates
 *
 * Note: This function is future-aware for bookability checking. It only considers
 * coverage from NOW onwards, not historical coverage.
 */
const doesBlackoutCoverSchedulePeriod = (blackout: Schedule, schedule: Schedule): boolean => {
    // For CUSTOM schedules, verify blackout covers all specific dates
    if (schedule.repeat === BaseScheduleRepeat.CUSTOM) {
        if (!schedule.dates?.length) {
            return true; // No dates = nothing to cover
        }

        if (!blackout.end && !blackout.expiresAt) {
            return true; // Permanent blackout covers all dates
        }

        const blackoutEnd = blackout.end ?? blackout.expiresAt;
        const blackoutEndDate = blackoutEnd ? now(blackoutEnd).format(DateFormat.DATE_ISO) : null;

        if (!blackoutEndDate) {
            return true;
        }

        return schedule.dates.every((date) => date <= blackoutEndDate);
    }

    // For WEEKLY schedules: Use future-aware coverage check

    const currentDate = now();
    const blackoutStartDate = blackout.start ? now(blackout.start) : null;
    const blackoutEnd = blackout.end ?? blackout.expiresAt ?? null;
    const blackoutEndDate = blackoutEnd ? now(blackoutEnd) : null;
    const scheduleStartDate = schedule.start ? now(schedule.start) : null;

    // For bookability, we only care about coverage from now onwards
    const effectiveScheduleStart = scheduleStartDate?.isAfter(currentDate, "day") ? scheduleStartDate : currentDate;

    // For WEEKLY schedules with a defined end date
    if (schedule.end) {
        const scheduleEndDate = now(schedule.end);

        // If schedule already ended, it's not relevant for future bookability
        if (scheduleEndDate.isBefore(currentDate, "day")) {
            return true;
        }

        // Blackout must start before or at effective start (no start = infinite past coverage)
        const coversStart = !blackoutStartDate || blackoutStartDate.isSameOrBefore(effectiveScheduleStart, "day");

        // Blackout must end after or at schedule end (no end = infinite future coverage)
        const coversEnd = !blackoutEndDate || blackoutEndDate.isSameOrAfter(scheduleEndDate, "day");

        return coversStart && coversEnd;
    }

    // For infinite schedules (no end date): only infinite blackouts can fully cover them
    if (!blackoutEndDate) {
        // Check if blackout starts before or at the effective start (from now onwards)
        return !blackoutStartDate || blackoutStartDate.isSameOrBefore(effectiveScheduleStart, "day");
    }

    // Finite blackout (end or expiresAt) cannot fully cover infinite schedule
    return false;
};

/**
 * Check if all available times are covered by blackout time ranges.
 * If blackouts have no timeRanges, they're all-day blackouts = all times covered.
 */
const areAllTimesCovered = (times: Array<number | null>, blackouts: Schedule[]): boolean => {
    const allTimeRanges = blackouts.flatMap((b) => b.timeRanges ?? []);

    // No time ranges = all-day blackout
    if (allTimeRanges.length === 0) {
        return true;
    }

    return times.every((time) => {
        if (time === null) return false;
        return allTimeRanges.some((range) => time >= range.startTime && time < range.endTime);
    });
};

/**
 * Check if applicable blackouts fully cover a schedule's times.
 * All-day blackout (no timeRanges) covers everything.
 * Time-range blackout must cover all the schedule's times.
 */
const doesBlackoutCoverTimes = (schedule: Schedule, applicableBlackouts: Schedule[]): boolean => {
    if (applicableBlackouts.length === 0) {
        return false;
    }

    const hasAllDayBlackout = applicableBlackouts.some((b) => !b.timeRanges || b.timeRanges.length === 0);
    if (hasAllDayBlackout) {
        return true;
    }

    // Schedule has specific times — check if all are within blackout time ranges
    if (schedule.times?.length) {
        return areAllTimesCovered(schedule.times, applicableBlackouts);
    }

    // Schedule has no times (all-day) but blackout has timeRanges — not fully covered
    return false;
};

/**
 * Check if a day-of-week is fully covered by blackouts for a WEEKLY schedule.
 */
const isDayCoveredByBlackouts = (day: number, schedule: Schedule, blackouts: Schedule[]): boolean => {
    const applicableBlackouts = blackouts.filter(
        (blackout) => blackout.days?.includes(day) && doesBlackoutCoverSchedulePeriod(blackout, schedule),
    );

    return doesBlackoutCoverTimes(schedule, applicableBlackouts);
};

const isDateCoveredByBlackouts = (date: string, schedule: Schedule, blackouts: Schedule[]): boolean => {
    const dateObj = dayjs(date);
    const applicableBlackouts = blackouts.filter((blackout) => includesDate(blackout, dateObj));

    return doesBlackoutCoverTimes(schedule, applicableBlackouts);
};

const isWeeklyScheduleFullyCovered = (schedule: Schedule, blackouts: Schedule[]): boolean => {
    const days = schedule.days ?? [];

    // No days specified means all days - check all 7 days
    const daysToCheck = days.length > 0 ? days : [0, 1, 2, 3, 4, 5, 6];

    return daysToCheck.every((day) => isDayCoveredByBlackouts(day, schedule, blackouts));
};

const isCustomScheduleFullyCovered = (schedule: Schedule, blackouts: Schedule[]): boolean => {
    const dates = schedule.dates ?? [];
    if (dates.length === 0) {
        return false; // No dates = nothing to cover
    }

    return dates.every((date) => isDateCoveredByBlackouts(date, schedule, blackouts));
};

const isScheduleFullyCovered = (schedule: Schedule, blackouts: Schedule[]): boolean => {
    if (schedule.repeat === BaseScheduleRepeat.CUSTOM) {
        return isCustomScheduleFullyCovered(schedule, blackouts);
    }

    return isWeeklyScheduleFullyCovered(schedule, blackouts);
};

/**
 * Determine bookability status from an array of schedules.
 */
export const getBookabilityStatus = (schedules: Schedule[]): BookabilityStatus => {
    const availableSchedules = getAvailableSchedules({ schedules });
    if (availableSchedules.length === 0) {
        // If there are no available schedules, only show FULL_BLACKOUT if there's
        // an infinite all-day blackout that has already started
        // Otherwise, the experience is bookable at other times/dates
        const blackouts = getActiveBlackoutSchedules(schedules ?? []);

        const hasInfiniteAllDayBlackout = blackouts.some((blackout) => {
            const isAllDay = !blackout.timeRanges || blackout.timeRanges.length === 0;
            // Check if blackout has already started (not in the future)
            const isStarted = !blackout.start || now(blackout.start).isSameOrBefore(now(), "day");
            const blackoutEnd = blackout.end ?? blackout.expiresAt;
            const hasEnd = !!blackoutEnd;

            return !hasEnd && isAllDay && isStarted;
        });

        if (hasInfiniteAllDayBlackout) {
            return BookabilityStatus.FULL_BLACKOUT;
        }

        return BookabilityStatus.BOOKABLE;
    }

    const activeSchedules = availableSchedules.filter((schedule) => isActive(schedule));
    if (activeSchedules.length === 0) {
        return BookabilityStatus.EXPIRED;
    }

    const blackouts = getActiveBlackoutSchedules(schedules ?? []);
    if (blackouts.length === 0) {
        return BookabilityStatus.BOOKABLE;
    }

    if (activeSchedules.every((schedule) => isScheduleFullyCovered(schedule, blackouts))) {
        return BookabilityStatus.FULL_BLACKOUT;
    }

    return BookabilityStatus.BOOKABLE;
};

/**
 * Determines bookability status considering both experience-level and seller-level schedules.
 *
 * @param experienceSchedules - The experience's schedules array (both available and blackout)
 * @returns BookabilityStatus - BOOKABLE, EXPIRED, or FULL_BLACKOUT
 */
export const useBookabilityStatus = (experienceSchedules: Schedule[]): BookabilityStatus => {
    const seller = getSeller();

    return useMemo(() => {
        const allSchedules = [...experienceSchedules, ...(seller.blackoutSchedules ?? [])];
        return getBookabilityStatus(allSchedules);
    }, [experienceSchedules, seller.blackoutSchedules]);
};
